import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { AutoTopup, AutoTopupSetup, Checkout } from '@arcnet/contracts';

export const autoTopupKeys = {
  status: (tenantId: string) => ['autotopup', tenantId] as const,
};

/** An account's auto top-up, watched while a card is being saved until PayFast confirms it. */
export const useAutoTopup = (tenantId: string | null) =>
  useQuery({
    queryKey: autoTopupKeys.status(tenantId ?? ''),
    queryFn: () => api.get<AutoTopup>(`/v1/tenants/${tenantId}/billing/autotopup`),
    enabled: !!tenantId,
    refetchInterval: (q) => (q.state.data?.card?.status === 'pending' ? 4000 : false),
  });

/** Saves a card with a purchase of the top-up amount: a signed PayFast checkout. */
export function useSaveCard(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutoTopupSetup) => api.post<Checkout>(`/v1/tenants/${tenantId}/billing/autotopup`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: autoTopupKeys.status(tenantId) });
      void qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export function usePauseAutoTopup(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.put<AutoTopup>(`/v1/tenants/${tenantId}/billing/autotopup`, { enabled }),
    onSuccess: (saved) => { qc.setQueryData(autoTopupKeys.status(tenantId), saved); },
  });
}

/** Turns auto top-up off and forgets the card. */
export function useRemoveCard(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/v1/tenants/${tenantId}/billing/autotopup`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: autoTopupKeys.status(tenantId) }); },
  });
}
